import React from 'react';
import { MenuItem, Stack, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { fetchShareGroups } from '../api/shareGroups';
import { ShareScope } from '../types';

/** Narrowest first, so the list reads as a widening dial rather than an
 * arbitrary set — and so the safe option is the one nearest the top. Each
 * carries what it actually means, because "group" and "public" are the two
 * people guess wrong about. */
const SCOPES: { value: ShareScope; label: string; hint: string }[] = [
  { value: 'private', label: 'Only me', hint: 'Nobody else can see this.' },
  { value: 'group', label: 'A share group', hint: 'Only the characters in the group you pick.' },
  { value: 'corp', label: 'My corp', hint: 'Everyone in your corporation.' },
  { value: 'alliance', label: 'My alliance', hint: 'Everyone in your alliance.' },
  {
    value: 'public',
    label: 'Everyone here',
    hint: 'Anyone who can log into this app. Still gated by EVE SSO and the corp/alliance allowlist — not the public internet.',
  },
];

const ShareScopeSelect: React.FC<{
  value: ShareScope;
  groupId: number | null;
  onChange: (scope: ShareScope, groupId: number | null) => void;
}> = ({ value, groupId, onChange }) => {
  const { data: groups } = useQuery({ queryKey: ['share-groups'], queryFn: fetchShareGroups, enabled: value === 'group' });

  return (
    <Stack direction="row" spacing={1}>
      <TextField
        size="small"
        select
        label="Share with"
        value={value}
        onChange={(e) => onChange(e.target.value as ShareScope, null)}
        sx={{ width: 170 }}
        helperText={SCOPES.find((s) => s.value === value)?.hint}
        FormHelperTextProps={{ sx: { fontSize: '0.66rem', mx: 0, maxWidth: 260 } }}
      >
        {SCOPES.map((s) => (
          <MenuItem key={s.value} value={s.value}>
            {s.label}
          </MenuItem>
        ))}
      </TextField>
      {value === 'group' && (
        <TextField
          size="small"
          select
          label="Group"
          value={groupId ?? ''}
          onChange={(e) => onChange('group', Number(e.target.value))}
          sx={{ width: 160 }}
        >
          {(groups ?? []).map((g) => (
            <MenuItem key={g.id} value={g.id}>
              {g.name}
            </MenuItem>
          ))}
        </TextField>
      )}
    </Stack>
  );
};

export default ShareScopeSelect;
